import { sectionIdAtTime } from './sectionIdAtTime';

import type { ReelSection } from './deriveReelSections';

/**
 * How far inside a Section's end the playhead is kept when the offset it
 * carried would otherwise land past it. A time exactly on a boundary belongs to
 * the Section that *starts* there (`sectionIdAtTime`), so landing on one would
 * mark the following Section — the very confusion this move exists to undo.
 */
const BOUNDARY_MARGIN_SECONDS = 0.01;

/**
 * PLAT-11148 — where the playhead belongs once a drop has reordered the reel.
 *
 * A reorder moves passages around in time but leaves the playhead at the time it
 * was already at, so the moment the creator was hearing now holds whatever was
 * dragged into it. The panel marks the Section under the playhead (PLAT-11073),
 * which is why the marking read as having stayed behind on the old row while the
 * passage it belonged to moved away.
 *
 * Following the passage is the fix: the playhead keeps its offset into the
 * Section it was already inside and lands wherever that Section now starts, so
 * the marking and what is being heard both stay with the creator's place in the
 * reel.
 *
 * `null` when there is nothing to follow — no Sections, or that Section starts
 * exactly where it did before (a drop below it, or one that came back to its own
 * place), in which case the playhead is already where it belongs.
 */
export function reelPlayheadAfterReorder(
  before: readonly ReelSection[],
  after: readonly ReelSection[],
  time: number,
): number | null {
  const activeId = sectionIdAtTime(before, time);
  if (activeId === undefined) return null;

  const from = before.find(section => section.id === activeId);
  const to = after.find(section => section.id === activeId);
  // Nothing here removes a Section, so a missing one means the two lists were
  // read a beat apart — leave the playhead alone rather than guess at a place.
  if (!from || !to || from.startTime === to.startTime) return null;

  // Past the reel's end the playhead still counts as inside the last Section
  // (`sectionIdAtTime`), so the offset it carries can be longer than the passage
  // it is being applied to.
  const furthest = Math.max(to.duration - BOUNDARY_MARGIN_SECONDS, 0);
  const offset = Math.min(Math.max(time - from.startTime, 0), furthest);
  return to.startTime + offset;
}
